import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import useInput from 'src/hooks/useInput'
import Cookies from 'js-cookie'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useScrollTrigger
} from '@material-ui/core'
import {
  VisibilityOutlined,
  VisibilityOffOutlined,
  Error,
  DescriptionOutlined,
  ExitToAppOutlined,
  DashboardOutlined,
  AccountBalanceOutlined,
  PeopleOutlined,
  BookOutlined,
  AttachMoneyOutlined
} from '@material-ui/icons'
import {
  PATRIMONY_PERMISSIONS,
  NEWS_PERMISSIONS,
  HR_PERMISSIONS,
  LIBRARY_PERMISSIONS,
  QUOTA_PERMISSIONS,
  ADMIN_PERMISSIONS
} from 'src/components/NewsModule/constants'
import usePersistedState from 'src/hooks/usePersistedState'
import { QueryGetValue } from 'src/utils/General/QueryGetValue'
import { postLogin } from 'src/services/api/WebApi/Auth/auth.service'
import { ReactComponent as ManageAccountsIcon } from 'src/assets/icons/login/manage-accounts-icon.svg'
import { getApprovers } from 'src/services/api/WebApi/Approvers/approvers.service'
import { useStyles } from './LoginModal.styles'

interface Props {
  setLoggedIn(data: any): void
  openModal(isOpen: boolean): void
  loggedIn: any
}

const ModalLogin = ({ loggedIn, setLoggedIn, openModal }: Props) => {
  const { t } = useTranslation()
  const trigger = useScrollTrigger()

  const [validCredential, setValidCredential] = useState<boolean>(true)
  const [isEnable, setIsEnable] = useState<boolean>(true)
  const [loadingLogin, setLoadingLogin] = useState<boolean>(false)

  const email = useInput('')
  const password = useInput('')
  const [isEyeOpen, setIsEyeOpen] = useState(false)

  const [management, setManagement] = usePersistedState('management', {
    access: false,
    link: '/'
  })

  const [approvers, setApprovers] = usePersistedState('approvers', [])

  const [systemAccess, setSystemAccess] = usePersistedState('system-access', {
    project: false,
    patrimony: false,
    hr: false,
    library: false,
    quota: false
  })
  const classes = useStyles()

  const logout = () => {
    localStorage.clear()
    Cookies.remove('auth-token', {
      path: '/',
      domain: process.env.REACT_APP_DOMAIN
    })
    openModal(false)
    window.location.reload()
  }

  const handleEye = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsEyeOpen(!isEyeOpen)
  }

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendRequestLogin(email.value, password.value).then()
    }
  }

  const sendRequestApprovers = async (user: any) => {
    getApprovers({ userId: user.id })
      .then((response) => {
        setApprovers(response.data.map((e: any) => e.id))
      })
      .catch((err) => {
        console.log(err)
      })
      .finally(() => {
        setLoadingLogin(false)
        window.location.reload()
      })
  }

  const getSystemAccess = (user: any) => {
    const accessProject = user.profileId.length > 0
    const accessPatrimony = getSystemPermissions(user, PATRIMONY_PERMISSIONS)
    const accessHr = getSystemPermissions(user, HR_PERMISSIONS)
    const accessNews = getNewsPermissions(user, NEWS_PERMISSIONS)
    const accessLibrary = getSystemPermissions(user, LIBRARY_PERMISSIONS)
    const accessQuota = getSystemPermissions(user, QUOTA_PERMISSIONS)
    const accessAdmin = getSystemPermissions(user, ADMIN_PERMISSIONS)

    setSystemAccess({
      project: accessProject,
      patrimony: accessPatrimony,
      hr: accessHr,
      library: accessLibrary,
      quota: accessQuota,
      admin: accessAdmin
    })
    setManagement(accessNews)
  }

  const getNewsPermissions = (user: any, systemPermissions: any) => {
    for (let i = 0; i < systemPermissions.length; i++) {
      const aux = user.generalPermissions.find(
        (s: any) => s.screen === systemPermissions[i].screen
      )
      if (aux && aux.allowRead) {
        return { access: true, link: systemPermissions[i].route }
      }
    }
    return { access: false, link: '/' }
  }

  const getSystemPermissions = (user: any, systemPermissions: any) => {
    for (let i = 0; i < systemPermissions.length; i++) {
      const aux = user.generalPermissions.find(
        (s: any) => s.screen === systemPermissions[i].screen
      )
      if (aux && aux.allowRead) {
        return true
      }
    }
    return false
  }

  const handleRequest = (
    e: React.MouseEvent<HTMLSpanElement>,
    email: string,
    password: string
  ) => {
    e.stopPropagation()
    sendRequestLogin(email, password).then()
  }

  const sendRequestLogin = async (email: string, password: string) => {
    setLoadingLogin(true)
    postLogin({ email, password })
      .then((response) => {
        Cookies.set('auth-token', response.data.token, {
          path: '/',
          domain: process.env.REACT_APP_DOMAIN
        })
        setLoggedIn(response.data.authData)
        getSystemAccess(response.data.authData)
        sendRequestApprovers(response.data.authData)
        openModal(false)
      })
      .catch((err) => {
        console.log(err)
        setValidCredential(false)
      })
      .finally(() => {
        setLoadingLogin(false)
      })
  }

  useEffect(() => {
    setIsEnable(true)
    setValidCredential(true)
  }, [email.value, password.value])

  const [queryString, setQueryString] = useState('')

  useEffect(() => {
    setQueryString(QueryGetValue('logout') || '')
  }, [])

  useEffect(() => {
    if (queryString) {
      setLoggedIn(false)
      setQueryString('')
    }
  }, [queryString])

  useEffect(() => {
    openModal(false)
  }, [trigger])

  return (
    <>
      {!loggedIn ? (
        <>
          <form noValidate autoComplete="on" className={classes.sidebarForm}>
            <Typography variant="h6" className={classes.title}>
              {t('login-modal.title')}
            </Typography>
            <Box className={classes.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                htmlFor="email-lsbd"
                color="textPrimary"
                className={classes.inputLabel}
              >
                {t('login-modal.email')}:
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                className={classes.input}
                onChange={email.onChange}
                type="email"
                placeholder={t('login-modal.email-label')}
                onKeyDown={handleKey}
                id="email-lsbd"
                label={null}
                error={!validCredential}
              />
            </Box>
            <Box className={classes.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                htmlFor="password-lsbd"
                color="textPrimary"
                className={classes.inputLabel}
              >
                {t('login-modal.password')}:
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                className={classes.input}
                onChange={password.onChange}
                type={isEyeOpen ? 'text' : 'password'}
                placeholder={t('login-modal.password-label')}
                onKeyDown={handleKey}
                id="password-lsbd"
                label={null}
                error={!validCredential}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleEye}
                        size="small"
                        edge="end"
                        disableFocusRipple={true}
                      >
                        {isEyeOpen ? (
                          <VisibilityOutlined fontSize="small" />
                        ) : (
                          <VisibilityOffOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  className: classes.inputText
                }}
              />
            </Box>
            {!validCredential && (
              <Box className={classes.invalidCredential}>
                <Error color="error" />
                <Typography color="textPrimary" variant="body2">
                  {t('login-modal.invalid-credentials')}
                </Typography>
              </Box>
            )}
            {!isEnable && (
              <Box className={classes.invalidCredential}>
                <Error color="error" />
                <Typography color="textPrimary" variant="body2">
                  {t('login-modal.user-disabled')}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              className={classes.loginButton}
              onClick={() => sendRequestLogin(email.value, password.value)}
              disabled={loadingLogin}
            >
              {!loadingLogin ? (
                <Typography
                  variant="h6"
                  className={classes.loginButtonText}
                  onClick={(e) => handleRequest(e, email.value, password.value)}
                >
                  Login
                </Typography>
              ) : (
                <CircularProgress size={16} />
              )}
            </Button>
            <Link
              component={RouterLink}
              to="/forgot-password"
              color="textPrimary"
              underline="hover"
              variant="body2"
            >
              {t('login-modal.forgot-password')}
            </Link>
          </form>
        </>
      ) : (
        <>
          <Box className={classes.sidebarLoggedItem}>
            <Grid
              container
              direction="row"
              alignItems="center"
              className={classes.gridLoggedItem}
              spacing={3}
            >
              {systemAccess.admin && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'Admin'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ManageAccountsIcon />
                    {t('sidebar-logged.administrative')}
                  </Link>
                </Grid>
              )}
              {systemAccess.project && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'Projects'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                  >
                    <DashboardOutlined />
                    {t('sidebar-logged.project')}
                  </Link>
                </Grid>
              )}
              {systemAccess.patrimony && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'Patrimony'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                  >
                    <AccountBalanceOutlined />
                    {t('sidebar-logged.heritage')}
                  </Link>
                </Grid>
              )}
              {systemAccess.hr && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'HumanResources'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                  >
                    <PeopleOutlined />
                    {t('sidebar-logged.people-management')}
                  </Link>
                </Grid>
              )}
              {systemAccess.library && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'Library'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                  >
                    <BookOutlined />
                    {t('sidebar-logged.library')}
                  </Link>
                </Grid>
              )}
              {systemAccess.quota && (
                <Grid item xs>
                  <Link
                    href={process.env.REACT_APP_LSBD_SYSTEM + 'Quotas'}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                    target="_blank"
                  >
                    <AttachMoneyOutlined />
                    {t('sidebar-logged.quotas')}
                  </Link>
                </Grid>
              )}
              {management.access && (
                <Grid item xs>
                  <Link
                    component={RouterLink}
                    to={management.link}
                    underline="none"
                    className={classes.loggedItem}
                    variant="body2"
                  >
                    <DescriptionOutlined />
                    {t('sidebar-logged.news-module')}
                  </Link>
                </Grid>
              )}
              <Grid item xs>
                <Link
                  className={classes.loggedItem}
                  underline="none"
                  onClick={() => logout()}
                  variant="body2"
                >
                  <ExitToAppOutlined />
                  {t('sidebar-logged.logout')}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
}

export default ModalLogin
